import type { CoworkerGridRowType, EditableContractHoursCellProps, OrgSelection } from 'types/appContext';
import type { DummyCoworker } from 'types/scenario';
import EditableCell from 'components/EditableCell';
import { roundDisplayValueDoubleDecimal } from 'utils/number';
import useData from 'hooks/useData';
import useApp from 'hooks/useApp';
import { useUpdateScenarioAPI } from 'hooks/api/useUpdateScenarioAPI';
import { getContractHoursRange } from 'components/CoworkerDetails/helpers';
import { getGreyedOutCell } from 'components/GreyedOutCell/helpers';
import { useMemo } from 'react';
import { RangeStatus } from 'types/enums';
import { type Coworker, EmploymentStatus } from 'types/coworker';
import { useTranslation } from 'react-i18next';
import { costCentreIsInList } from 'utils/text';
import Text from '@ingka/text';
import { ModulationSource } from 'types/dataContext';
import CoworkerNameCell from './CoworkerNameCell';
import classes from './ContractHoursRow.module.scss';
import { ContractDetails } from './ContractDetails';

const getDivisionName = (costCentre: string, coworker: Coworker | DummyCoworker, hierarchy: OrgSelection['utils']['hierarchy']) => {
    if (coworker?.costCentre === costCentre && coworker.divisionName) {
        return coworker.divisionName;
    }
    if (!hierarchy?.costCentreHierarchy) {
        return '';
    }

    return hierarchy?.costCentreHierarchy.find(({ costCentre: cc }) => cc.costCentreCode === costCentre)?.division?.name ?? '';
};

export const ContractHoursRow = ({
    coworker,
    showAllDetails,
    cellProps,
    canEdit,
    averageHrs,
    costCentre,
    handleClickDetails,
    isPrimaryRow,
    isActive,
    totalHours,
    contractStartDate,
    contractEndDate,
}: {
    coworker: Coworker | DummyCoworker,
    showAllDetails: boolean,
    cellProps: Array<EditableContractHoursCellProps>,
    canEdit: boolean,
    averageHrs: number,
    totalHours: {
        contractHours: number,
    },
    costCentre: string,
    handleClickDetails: () => void,
    isPrimaryRow: boolean,
    isActive: boolean,
    contractStartDate: string,
    contractEndDate: string,
}) => {
    const { currentScenario, modulationData } = useData();
    const { timeSelection, orgSelection: { utils: { selectedCostCentres, hierarchy } } } = useApp();
    const { updateScenario } = useUpdateScenarioAPI();
    const { t } = useTranslation();
    const isDisabled = useMemo(() => (
        (!currentScenario && !isActive) // Allow changes on inactive coworkers in a scenario
    || !selectedCostCentres.length
    || !costCentreIsInList(costCentre, selectedCostCentres.map(cc => cc.costCentreCode))
    ), [currentScenario, isActive, selectedCostCentres, costCentre]);

    const tooltipMessage = useMemo(() => (hoursType: CoworkerGridRowType | ModulationSource | undefined) => {
        if (!selectedCostCentres.length || !costCentreIsInList(costCentre, selectedCostCentres.map(cc => cc.costCentreCode))) {
            return t('COST_CENTRE_NOT_SELECTED');
        }
        if (modulationData) {
            if (hoursType === ModulationSource.SCHEDULE) {
                return t('SCHEDULED_HOURS_TOOLTIP');
            }

            if (hoursType === ModulationSource.ACTUALS) {
                return t('WORKED_HOURS_TOOLTIP');
            }

            if (hoursType === ModulationSource.PLANNED) {
                return t('PLANNED_HOURS_TOOLTIP');
            }

            return t('CONTRACT_HOURS');
        }
        if (!isDisabled && canEdit) {
            return t('CHANGE_AVAILABILITY_DESCRIPTION');
        }

        return undefined;
    }, [canEdit, costCentre, isDisabled, selectedCostCentres, t, modulationData]);

    const cells = useMemo(
        () => cellProps.map((cProps, index) => {
            const { elementId, originalValue, currentValue, key, coworkerAvailability, hoursType } = cProps;
            const contractAbsentCell = getGreyedOutCell(
                costCentre,
                contractStartDate,
                contractEndDate,
                timeSelection.timeArray,
                index,
                key,
                'futureData' in coworker ? coworker.futureData : undefined,
            );

            if (contractAbsentCell !== RangeStatus.OUTSIDE_RANGE && hoursType !== ModulationSource.SCHEDULE) {
                return contractAbsentCell;
            }

            return (
                <EditableCell
                    elementId={elementId}
                    originalValue={originalValue}
                    currentValue={currentValue}
                    canBeEdited={canEdit}
                    key={key}
                    coworkerAvailability={coworkerAvailability}
                    isDisabled={isDisabled}
                    coworker={coworker}
                    updateScenario={updateScenario}
                    tooltipMessage={tooltipMessage(hoursType)}
                    contractStartDate={contractStartDate}
                    contractEndDate={contractEndDate}
                    timeArray={timeSelection.timeArray}
                />
            );
        }).filter(el => el.toString() !== RangeStatus.OUTSIDE_RANGE && el.toString() !== RangeStatus.WITHIN_RANGE),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cellProps, canEdit, isDisabled, tooltipMessage]
    );

    return (
        <tr className={(coworker.status === EmploymentStatus.ACTIVE || currentScenario) ? '' : classes.disabled}>
            {isPrimaryRow ? (
                <td>
                    <div className={classes['complex-row-cell']}>
                        <CoworkerNameCell
                            coworker={coworker}
                            handleClickCoworker={handleClickDetails}
                            key={coworker.personId}
                            isActive={isActive}
                            showModulationIcon={(coworker as Coworker).isModulated}
                        />
                        <div data-testid="coworker-contract-hours">
                            <Text textStyle="Body.M">
                                {!coworker.contractRange
                                    ? roundDisplayValueDoubleDecimal(coworker.hoursPerWeek ?? 0)
                                    : getContractHoursRange(coworker)}
                            </Text>
                        </div>
                    </div>
                </td>

            ) : (<td aria-labelledby="coworker-name-dummy" className={coworker.isDummy && isPrimaryRow ? classes['coworker-name-dummy'] : ''} />)}
            {showAllDetails && (
            <ContractDetails divisionName={getDivisionName(costCentre, coworker, hierarchy)} costCentre={costCentre} />
            )}
            {cells}
            <td>
                <div className={classes['table-row__footer']}>
                    <div className={classes['table-row__footer__cell']} data-testid="coworker-average-hours">
                        {roundDisplayValueDoubleDecimal(averageHrs)}
                    </div>
                    <div className={classes['table-row__footer__cell']} data-testid="coworker-total-hours">
                        { roundDisplayValueDoubleDecimal(totalHours.contractHours)}
                    </div>
                </div>
            </td>
        </tr>
    );
};
